// src/components/CheckoutModal.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { X, Shield, Lock, ChevronRight, Check, AlertCircle, User, Sword } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { API } from "../utils/constants";
import { useCurrency, fmtPrice } from "../hooks/useCurrency";
import { useAuth } from "../context/AuthContext";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK || "");

// When MC is needed, steps are: Order / Link / Payment / Confirm
// When MC is already set, steps are:  Order / Payment / Confirm
const STEPS_WITH_MC    = ["Order", "Link MC", "Payment", "Confirm"];
const STEPS_WITHOUT_MC = ["Order", "Payment", "Confirm"];

/* ── Minecraft block border (unchanged) ─────────────────────────────────── */
const MC_PAL = [
  "#1a1530","#2a2040","#130f22","#3a2d54","#2a1f42",
  "#7b5ea7","#5a3d8a","#6a4e98","#4a2d72","#7b5ea7",
  "#2a2040","#1a1530","#0d0b18","#2a1f42","#3a2d54",
  "#5a3d8a","#7b5ea7","#4a2d72","#9b7fd4","#5a3d8a",
  "#130f22","#2a2040","#1a1530","#3a2d54","#110e1e",
  "#7b5ea7","#5a3d8a","#2a2040","#6a4e98","#7b5ea7",
];
const BLOCK = 8;

function McStripH({ count = 64 }) {
  return (
    <div style={{ display: "flex", height: BLOCK, flexShrink: 0, overflow: "hidden" }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ flex: 1, background: MC_PAL[i % MC_PAL.length], borderRight: "1px solid rgba(0,0,0,.5)" }} />
      ))}
    </div>
  );
}
function McStripV({ rows = 96 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", width: BLOCK, flexShrink: 0, alignSelf: "stretch" }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ flex: 1, background: MC_PAL[i % MC_PAL.length], borderBottom: "1px solid rgba(0,0,0,.5)", minHeight: 4 }} />
      ))}
    </div>
  );
}
function McFrame({ children, maxWidth = 480 }) {
  return (
    <div style={{ width: "100%", maxWidth, display: "flex", flexDirection: "column", boxShadow: "0 0 0 2px #0d0b18, 0 32px 100px rgba(0,0,0,.95)" }}>
      <McStripH count={64} />
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        <McStripV rows={96} />
        <div style={{ flex: 1, minWidth: 0, background: "linear-gradient(170deg,#0d0a1a,#110d22,#0a0810)", overflow: "hidden auto", maxHeight: "calc(95vh - 16px)" }}>
          {children}
        </div>
        <McStripV rows={96} />
      </div>
      <McStripH count={64} />
    </div>
  );
}

function StepIndicator({ step, steps }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 28 }}>
      {steps.map((label, i) => {
        const done   = i < step;
        const active = i === step;
        return (
          <div key={label} style={{ display: "flex", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: done ? "linear-gradient(135deg,#c9a84c,#8a6020)" : active ? "linear-gradient(135deg,#7b5ea7,#4a2d6a)" : "rgba(255,255,255,.04)", border: `2px solid ${done ? "#c9a84c" : active ? "#9b7fd4" : "#2a2040"}`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: active ? "0 0 18px rgba(123,94,167,.5)" : done ? "0 0 12px rgba(201,168,76,.3)" : "none", transition: ".3s" }}>
                {done ? <Check style={{ width: 13, height: 13, color: "#0d0800" }} /> : <span style={{ fontSize: 11, color: active ? "#e2d9f3" : "#3d3060", fontFamily: "'Cinzel',serif", fontWeight: 700 }}>{i + 1}</span>}
              </div>
              <span style={{ fontSize: 9, letterSpacing: 2, fontFamily: "'Cinzel',serif", textTransform: "uppercase", color: active ? "#c9a84c" : done ? "#9b7fd4" : "#3d3060" }}>{label}</span>
            </div>
            {i < steps.length - 1 && <div style={{ width: 40, height: 1, margin: "0 4px", marginBottom: 20, background: i < step ? "linear-gradient(90deg,#c9a84c,#7b5ea7)" : "#2a2040" }} />}
          </div>
        );
      })}
    </div>
  );
}

/* ── Step: Order Summary ─────────────────────────────────────────────────── */
function OrderStep({ cart, symbol, onNext }) {
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        {cart.map(item => (
          <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", marginBottom: 8, background: "rgba(255,255,255,.03)", border: `1px solid ${item.borderColor}`, position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, background: item.color }} />
            <div style={{ width: 36, height: 36, background: item.dimColor, border: `1px solid ${item.borderColor}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {item.type === "coins" ? <img src="/toa_coin_32.png" alt="coin" style={{ width: 24, height: 24, imageRendering: "pixelated" }} /> : <img src="/vip-icon.png" alt="rank" style={{ width: 28, height: 28, imageRendering: "pixelated", objectFit: "contain" }} />}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 11, color: item.color, fontWeight: 700, letterSpacing: 1 }}>{item.name} {item.subtitle}</div>
              <div style={{ fontSize: 10, color: "#6b6080", marginTop: 2 }}>{item.period || "One-time"}{item.qty > 1 ? ` · x${item.qty}` : ""}</div>
            </div>
            <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 14, color: "#e2d9f3" }}>{fmtPrice(item.price * item.qty, symbol)}</div>
          </div>
        ))}
      </div>
      <div style={{ padding: "14px 16px", background: "rgba(201,168,76,.06)", border: "1px solid rgba(201,168,76,.2)", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: "'Cinzel',serif", fontSize: 11, color: "#8b7fa0", letterSpacing: 2 }}>TOTAL DUE</span>
        <span style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 24, color: "#c9a84c" }}>{fmtPrice(total, symbol)}</span>
      </div>
      <div style={{ fontSize: 10, color: "#5a5070", lineHeight: 1.7, marginBottom: 20, fontFamily: "'Cinzel',serif", letterSpacing: .3 }}>
        By proceeding you agree to our terms of service. Ranks are subject to in-game rules. TOA Coins are non-refundable.
      </div>
      <button onClick={onNext} style={{ width: "100%", padding: "14px", background: "linear-gradient(135deg,#1a0e2e,#2a1548,#1a0e2e)", border: "1px solid rgba(123,94,167,.5)", color: "#e2d9f3", fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, transition: ".2s" }}
        onMouseOver={e => e.currentTarget.style.borderColor = "#9b7fd4"}
        onMouseOut={e => e.currentTarget.style.borderColor = "rgba(123,94,167,.5)"}
      >
        <Shield style={{ width: 14, height: 14, color: "#c9a84c" }} />
        Proceed to Payment
        <ChevronRight style={{ width: 14, height: 14 }} />
      </button>
    </div>
  );
}

/* ── Step: Link Minecraft ────────────────────────────────────────────────── */
function LinkMcStep({ accessToken, onLinked, onBack }) {
  const { refreshProfile } = useAuth();
  const [username, setUsername] = useState("");
  const [state,    setState]    = useState("idle"); // idle | searching | found | notfound | saving | error
  const [found,    setFound]    = useState(null);   // { playerName }
  const [errMsg,   setErrMsg]   = useState("");

  const search = useCallback(async () => {
    const q = username.trim();
    if (!q || q.length < 3) return;
    setState("searching");
    setFound(null);
    setErrMsg("");
    try {
      const r    = await fetch(`${API}/tracker/verify-mc?username=${encodeURIComponent(q)}`);
      const data = await r.json();
      if (data.exists) { setFound(data); setState("found"); }
      else             { setState("notfound"); }
    } catch {
      setState("error"); setErrMsg("Network error. Please try again.");
    }
  }, [username]);

  const confirm = async () => {
    if (!found) return;
    setState("saving");
    try {
      const r = await fetch(`${API}/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ minecraft_username: found.playerName }),
      });
      const data = await r.json();
      if (!r.ok) {
        setState("error");
        setErrMsg(data.errors?.[0]?.msg || data.error || "Failed to link account.");
        return;
      }
      await refreshProfile();
      onLinked(found.playerName);
    } catch {
      setState("error"); setErrMsg("Network error. Please try again.");
    }
  };

  const skinUrl = found ? `https://mc-heads.net/avatar/${encodeURIComponent(found.playerName)}/64` : null;

  return (
    <div>
      <div style={{ background: "rgba(74,222,128,.04)", border: "1px solid rgba(74,222,128,.2)", borderRadius: 6, padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Sword style={{ width: 14, height: 14, color: "#4ade80" }} />
          <span style={{ fontFamily: "'Cinzel',serif", fontSize: 11, color: "#4ade80", letterSpacing: 1 }}>LINK YOUR MINECRAFT ACCOUNT</span>
        </div>
        <p style={{ fontSize: 11, color: "#6b6080", margin: 0, fontFamily: "'Cinzel',serif", lineHeight: 1.7 }}>
          Enter your in-game username to link your account. You must have joined the server at least once.
        </p>
      </div>

      {/* Input + Search */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <div style={{ position: "relative", flex: 1 }}>
          <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#4a4060", pointerEvents: "none" }}>
            <User style={{ width: 14, height: 14 }} />
          </span>
          <input
            type="text"
            value={username}
            onChange={e => { setUsername(e.target.value); setState("idle"); setFound(null); }}
            onKeyDown={e => e.key === "Enter" && search()}
            placeholder="e.g. Steve"
            maxLength={16}
            style={{ width: "100%", background: "rgba(255,255,255,.04)", border: "1px solid rgba(123,94,167,.25)", padding: "10px 12px 10px 36px", fontSize: 13, color: "#e2d9f3", fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box" }}
            onFocus={e => e.target.style.borderColor = "rgba(123,94,167,.6)"}
            onBlur={e  => e.target.style.borderColor = "rgba(123,94,167,.25)"}
          />
        </div>
        <button
          onClick={search}
          disabled={state === "searching" || username.trim().length < 3}
          style={{ padding: "10px 18px", background: "linear-gradient(135deg,#7b5ea7,#5a3d8a)", border: "none", color: "#fff", fontFamily: "'Cinzel',serif", fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", cursor: state === "searching" ? "not-allowed" : "pointer", opacity: username.trim().length < 3 ? .4 : 1, flexShrink: 0 }}
        >
          {state === "searching" ? "..." : "Search"}
        </button>
      </div>

      {/* Result */}
      {state === "notfound" && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", background: "rgba(239,68,68,.06)", border: "1px solid rgba(239,68,68,.25)", marginBottom: 14 }}>
          <AlertCircle style={{ width: 14, height: 14, color: "#ef4444", flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: "#ef4444", fontFamily: "'Cinzel',serif" }}>
            Player not found. You must join the server first before linking.
          </span>
        </div>
      )}

      {state === "found" && found && (
        <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "rgba(74,222,128,.06)", border: "1px solid rgba(74,222,128,.3)", marginBottom: 14 }}>
          <img src={skinUrl} alt={found.playerName} style={{ width: 48, height: 48, imageRendering: "pixelated", flexShrink: 0 }} onError={e => { e.target.style.display = "none"; }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 13, color: "#e2d9f3", fontWeight: 700 }}>{found.playerName}</div>
            <div style={{ fontSize: 11, color: "#4ade80", marginTop: 3 }}>Found on the server</div>
          </div>
          <Check style={{ width: 18, height: 18, color: "#4ade80", flexShrink: 0 }} />
        </div>
      )}

      {(state === "error") && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "rgba(239,68,68,.06)", border: "1px solid rgba(239,68,68,.25)", marginBottom: 14 }}>
          <AlertCircle style={{ width: 13, height: 13, color: "#ef4444" }} />
          <span style={{ fontSize: 11, color: "#ef4444", fontFamily: "'Cinzel',serif" }}>{errMsg}</span>
        </div>
      )}

      <button
        onClick={confirm}
        disabled={state !== "found" && state !== "error"}
        style={{ width: "100%", padding: "14px", background: state === "found" ? "linear-gradient(135deg,#4ade80,#22c55e)" : "rgba(255,255,255,.04)", border: `1px solid ${state === "found" ? "rgba(74,222,128,.6)" : "#2a2040"}`, color: state === "found" ? "#0a1a0a" : "#3a3060", fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", cursor: state === "found" ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, transition: ".2s", marginBottom: 10 }}
      >
        {state === "saving" ? (
          <><div style={{ width: 14, height: 14, border: "2px solid rgba(0,0,0,.3)", borderTopColor: "#0a1a0a", borderRadius: "50%", animation: "co-spin .7s linear infinite" }} />Linking...</>
        ) : (
          <><Sword style={{ width: 14, height: 14 }} />Confirm & Continue to Payment</>
        )}
      </button>

      <button onClick={onBack} style={{ width: "100%", padding: "10px", background: "transparent", border: "1px solid #2a2040", color: "#6b6080", fontFamily: "'Cinzel',serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", transition: ".15s" }}
        onMouseOver={e => { e.currentTarget.style.borderColor = "#4a4060"; e.currentTarget.style.color = "#9b7fd4"; }}
        onMouseOut={e  => { e.currentTarget.style.borderColor = "#2a2040"; e.currentTarget.style.color = "#6b6080"; }}
      >Back to Order</button>
    </div>
  );
}

/* ── Step: Stripe Payment ────────────────────────────────────────────────── */
function StripePaymentForm({ cart, symbol, onSuccess, onBack }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [error,   setError]   = useState(null);
  const [loading, setLoading] = useState(false);
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setError(null);
    setLoading(true);

    const { error: submitErr } = await elements.submit();
    if (submitErr) { setError(submitErr.message); setLoading(false); return; }

    const { error: payErr } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });

    if (payErr) { setError(payErr.message); setLoading(false); }
    else        { onSuccess(); }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "8px 14px", background: "rgba(74,222,128,.06)", border: "1px solid rgba(74,222,128,.2)", marginBottom: 20 }}>
        <Lock style={{ width: 11, height: 11, color: "#4ade80" }} />
        <span style={{ fontSize: 10, color: "#4ade80", fontFamily: "'Cinzel',serif", letterSpacing: 1 }}>256-bit SSL · Powered by Stripe</span>
      </div>

      <PaymentElement options={{ layout: "tabs" }} />

      {error && (
        <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.3)", fontSize: 11, color: "#ef4444", fontFamily: "'Cinzel',serif" }}>{error}</div>
      )}

      <button onClick={handlePay} disabled={loading || !stripe} style={{ marginTop: 20, width: "100%", padding: "15px", background: loading ? "rgba(123,94,167,.2)" : "linear-gradient(135deg,#7b3faa,#5a2d8a,#7b3faa)", border: `1px solid ${loading ? "rgba(123,94,167,.3)" : "rgba(155,127,212,.6)"}`, color: loading ? "#6b6080" : "#fff", fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, transition: ".2s", boxShadow: loading ? "none" : "0 0 24px rgba(123,94,167,.35)" }}>
        {loading ? (
          <><div style={{ width: 14, height: 14, border: "2px solid rgba(123,94,167,.4)", borderTopColor: "#9b7fd4", borderRadius: "50%", animation: "co-spin .7s linear infinite" }} />Processing...</>
        ) : (
          <><Lock style={{ width: 13, height: 13 }} />Pay {fmtPrice(total, symbol)}</>
        )}
      </button>

      <button onClick={onBack} style={{ width: "100%", marginTop: 10, padding: "10px", background: "transparent", border: "1px solid #2a2040", color: "#6b6080", fontFamily: "'Cinzel',serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", transition: ".15s" }}
        onMouseOver={e => { e.currentTarget.style.borderColor = "#4a4060"; e.currentTarget.style.color = "#9b7fd4"; }}
        onMouseOut={e  => { e.currentTarget.style.borderColor = "#2a2040"; e.currentTarget.style.color = "#6b6080"; }}
      >Back to Order</button>
    </div>
  );
}

/* ── Step: Confirmation ──────────────────────────────────────────────────── */
const DISCORD_URL = "https://discord.gg/AzrauJnuqw";

function ConfirmStep({ cart, symbol, onClose }) {
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ width: 80, height: 80, borderRadius: "50%", background: "linear-gradient(135deg,#4ade80,#22c55e)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 0 8px rgba(74,222,128,.1), 0 0 0 16px rgba(74,222,128,.05)", animation: "seal-pop .4s cubic-bezier(.34,1.56,.64,1)", margin: "0 auto 24px" }}>
        <Check style={{ width: 36, height: 36, color: "#fff" }} />
      </div>

      <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 18, color: "#c9a84c", marginBottom: 6 }}>Order Sealed</div>
      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 11, color: "#6b6080", letterSpacing: 3, textTransform: "uppercase", marginBottom: 20 }}>The Creed Acknowledges Your Tribute</div>

      <div style={{ padding: "14px 18px", background: "rgba(74,222,128,.05)", border: "1px solid rgba(74,222,128,.2)", marginBottom: 20, textAlign: "left" }}>
        {cart.map(item => (
          <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,.04)" }}>
            <span style={{ fontSize: 11, color: item.color, fontFamily: "'Cinzel',serif" }}>{item.name} {item.subtitle}{item.qty > 1 ? ` x${item.qty}` : ""}</span>
            <span style={{ fontSize: 11, color: "#e2d9f3", fontFamily: "'Courier New',monospace" }}>{fmtPrice(item.price * item.qty, symbol)}</span>
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 10 }}>
          <span style={{ fontSize: 10, color: "#6b6080", fontFamily: "'Cinzel',serif", letterSpacing: 2 }}>TOTAL PAID</span>
          <span style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 16, color: "#c9a84c" }}>{fmtPrice(total, symbol)}</span>
        </div>
      </div>

      <p style={{ fontSize: 11, color: "#6b6080", lineHeight: 1.8, fontFamily: "'Cinzel',serif", marginBottom: 20 }}>
        Your rank &amp; perks will be applied within 5 minutes. Reach out on Discord if anything is amiss.
      </p>

      <a href={DISCORD_URL} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "11px", background: "#5865F2", color: "#fff", fontFamily: "'Cinzel',serif", fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer", textDecoration: "none", marginBottom: 10 }}>
        <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 14, height: 14 }}><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/></svg>
        Join Discord
      </a>

      <button onClick={onClose} style={{ width: "100%", padding: "10px", background: "transparent", border: "1px solid #2a2040", color: "#6b6080", fontFamily: "'Cinzel',serif", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer" }}>
        Return to Store
      </button>
    </div>
  );
}

/* ── Stripe appearance ───────────────────────────────────────────────────── */
const STRIPE_APPEARANCE = {
  theme: "night",
  variables: { colorPrimary: "#9b7fd4", colorBackground: "#0d0a1a", colorText: "#e2d9f3", colorDanger: "#ef4444", fontFamily: "'Courier New', monospace", borderRadius: "0px" },
  rules: {
    ".Input":       { border: "1px solid rgba(123,94,167,.25)", backgroundColor: "rgba(255,255,255,.04)" },
    ".Input:focus": { border: "1px solid rgba(123,94,167,.6)",  boxShadow: "none" },
    ".Label":       { color: "#7a6aaa", fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase" },
  },
};

/* ── Main export ─────────────────────────────────────────────────────────── */
export default function CheckoutModal({ cart, onClose, onOpenAuth }) {
  const { symbol, loading: currencyLoading } = useCurrency();
  const { accessToken, authUser }            = useAuth();

  // Lock needsMc at mount — must not change mid-flow or step indices shift
  // and the user skips straight to Confirm when authUser updates after linking.
  const needsMcRef   = useRef(!authUser?.minecraft_username);
  const needsMc      = needsMcRef.current;
  const STEPS        = needsMc ? STEPS_WITH_MC : STEPS_WITHOUT_MC;

  // step indices depend on whether MC linking is in the flow
  // needsMc:    0=Order  1=LinkMC  2=Payment  3=Confirm
  // !needsMc:   0=Order  1=Payment 2=Confirm
  const PAYMENT_STEP = needsMc ? 2 : 1;
  const CONFIRM_STEP = needsMc ? 3 : 2;

  // Check if Stripe redirected back after a redirect-based payment (Klarna, Revolut etc.)
  // Stripe appends ?payment_intent=pi_xxx&redirect_status=succeeded to the return_url
  const redirectStatus   = new URLSearchParams(window.location.search).get('redirect_status');
  const redirectPiSecret = new URLSearchParams(window.location.search).get('payment_intent_client_secret');
  const redirectedSuccess = redirectStatus === 'succeeded' && !!redirectPiSecret;

  const [step,         setStep]         = useState(redirectedSuccess ? CONFIRM_STEP : 0);
  const [clientSecret, setClientSecret] = useState(redirectedSuccess ? redirectPiSecret : null);
  const [fetchError,   setFetchError]   = useState(null);

  // Clean up Stripe's URL params after reading them so the URL stays tidy
  useEffect(() => {
    if (!redirectedSuccess) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('payment_intent');
    url.searchParams.delete('payment_intent_client_secret');
    url.searchParams.delete('redirect_status');
    window.history.replaceState({}, '', url.toString());
  }, []); // eslint-disable-line

  // Fetch PaymentIntent when entering payment step
  useEffect(() => {
    if (step !== PAYMENT_STEP || clientSecret || fetchError) return;
    const items = cart.map(i => ({ id: i.id, qty: i.qty }));
    fetch(`${API}/checkout`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body:    JSON.stringify({ items }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.clientSecret) setClientSecret(data.clientSecret);
        else setFetchError(data.error || "Could not initialise payment.");
      })
      .catch(() => setFetchError("Network error - please try again."));
  }, [step, PAYMENT_STEP, cart, clientSecret, fetchError, accessToken]);

  // Stripe redirected back with a failed/cancelled payment
  const redirectFailed = !!redirectPiSecret && redirectStatus && redirectStatus !== 'succeeded';

  if (currencyLoading) return null;

  // Not logged in — show a gate instead of the checkout flow
  if (!authUser) {
    return (
      <div
        style={{ position: "fixed", inset: 0, zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 16px", background: "rgba(2,1,8,.75)", backdropFilter: "blur(6px)", animation: "toa-fadein .2s ease" }}
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <style>{`@keyframes toa-fadein { from{opacity:0} to{opacity:1} }`}</style>
        <McFrame maxWidth={400}>
          <div style={{ padding: "36px 32px", textAlign: "center" }}>
            <button onClick={onClose} style={{ position: "absolute", top: 16, right: 20, width: 28, height: 28, background: "rgba(255,255,255,.04)", border: "1px solid #2a2040", cursor: "pointer", color: "#4a4060", display: "flex", alignItems: "center", justifyContent: "center", transition: ".15s" }}
              onMouseOver={e => { e.currentTarget.style.borderColor = "#7b5ea7"; e.currentTarget.style.color = "#c9a84c"; }}
              onMouseOut={e  => { e.currentTarget.style.borderColor = "#2a2040"; e.currentTarget.style.color = "#4a4060"; }}
            >
              <X style={{ width: 11, height: 11 }} />
            </button>

            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(135deg,#7b5ea7,#4a2d6a)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 0 0 8px rgba(123,94,167,.1)" }}>
              <Lock style={{ width: 24, height: 24, color: "#c9a84c" }} />
            </div>

            <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 16, color: "#c9a84c", marginBottom: 8 }}>
              Account Required
            </div>
            <div style={{ fontFamily: "'Cinzel',serif", fontSize: 11, color: "#6b6080", letterSpacing: 1, lineHeight: 1.8, marginBottom: 28 }}>
              You must be logged in to purchase items from the store. Create a free account or sign in to continue.
            </div>

            <button
              onClick={() => { onClose(); onOpenAuth?.("login"); }}
              style={{ width: "100%", padding: "13px", marginBottom: 10, background: "linear-gradient(135deg,#7b5ea7,#5a3d8a)", border: "1px solid rgba(123,94,167,.5)", color: "#fff", fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", transition: ".2s", boxShadow: "0 0 20px rgba(123,94,167,.3)" }}
              onMouseOver={e => e.currentTarget.style.boxShadow = "0 0 30px rgba(123,94,167,.5)"}
              onMouseOut={e  => e.currentTarget.style.boxShadow = "0 0 20px rgba(123,94,167,.3)"}
            >
              Sign In
            </button>
            <button
              onClick={() => { onClose(); onOpenAuth?.("signup"); }}
              style={{ width: "100%", padding: "13px", background: "transparent", border: "1px solid rgba(123,94,167,.3)", color: "#9b7fd4", fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", cursor: "pointer", transition: ".2s" }}
              onMouseOver={e => { e.currentTarget.style.borderColor = "#9b7fd4"; e.currentTarget.style.color = "#c9a84c"; }}
              onMouseOut={e  => { e.currentTarget.style.borderColor = "rgba(123,94,167,.3)"; e.currentTarget.style.color = "#9b7fd4"; }}
            >
              Create Account
            </button>
          </div>
        </McFrame>
      </div>
    );
  }

  const stepTitle = () => {
    if (step === 0)            return "Review Order";
    if (needsMc && step === 1) return "Link Minecraft";
    if (step === PAYMENT_STEP) return "Secure Payment";
    return "Order Complete";
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 16px", background: "rgba(2,1,8,.75)", backdropFilter: "blur(6px)", animation: "toa-fadein .2s ease" }}
      onClick={e => { if (e.target === e.currentTarget && step !== PAYMENT_STEP) onClose(); }}
    >
      <style>{`
        @keyframes toa-fadein { from{opacity:0} to{opacity:1} }
        @keyframes co-spin    { to { transform: rotate(360deg) } }
        @keyframes seal-pop   { from { transform: scale(0) rotate(-180deg); opacity:0 } to { transform: scale(1) rotate(0deg); opacity:1 } }
      `}</style>

      <McFrame maxWidth={480}>
        <div style={{ padding: "20px 28px 0", position: "relative" }}>
          <button onClick={onClose} style={{ position: "absolute", top: 16, right: 20, width: 28, height: 28, background: "rgba(255,255,255,.04)", border: "1px solid #2a2040", cursor: "pointer", color: "#4a4060", display: "flex", alignItems: "center", justifyContent: "center", transition: ".15s" }}
            onMouseOver={e => { e.currentTarget.style.borderColor = "#7b5ea7"; e.currentTarget.style.color = "#c9a84c"; }}
            onMouseOut={e  => { e.currentTarget.style.borderColor = "#2a2040"; e.currentTarget.style.color = "#4a4060"; }}
          >
            <X style={{ width: 11, height: 11 }} />
          </button>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <img src="/toa-mc-logo.png" alt="The Ones Above" style={{ width: 64, height: 64, imageRendering: "pixelated", filter: "drop-shadow(0 0 10px rgba(201,168,76,.4))", display: "block", margin: "0 auto 8px" }} />
            <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 18, color: "#e2d9f3" }}>{stepTitle()}</div>
          </div>
          <StepIndicator step={step} steps={STEPS} />
        </div>

        <div style={{ padding: "0 28px 24px" }}>
          {step === 0 && (
            <OrderStep cart={cart} symbol={symbol} onNext={() => setStep(needsMc ? 1 : PAYMENT_STEP)} />
          )}

          {needsMc && step === 1 && (
            <LinkMcStep
              accessToken={accessToken}
              onLinked={() => setStep(PAYMENT_STEP)}
              onBack={() => setStep(0)}
            />
          )}

          {step === PAYMENT_STEP && (
            (redirectFailed || fetchError) ? (
              <div style={{ padding: "20px 0", textAlign: "center" }}>
                <AlertCircle style={{ width: 32, height: 32, color: "#ef4444", margin: "0 auto 12px", display: "block" }} />
                <div style={{ color: "#ef4444", fontFamily: "'Cinzel',serif", fontSize: 11, marginBottom: 16 }}>
                  {redirectFailed ? "Payment was not completed. Please try again." : fetchError}
                </div>
                <button onClick={() => { setFetchError(null); setClientSecret(null); setStep(0); }} style={{ padding: "10px 20px", background: "transparent", border: "1px solid rgba(239,68,68,.4)", color: "#ef4444", fontFamily: "'Cinzel',serif", fontSize: 10, letterSpacing: 2, cursor: "pointer" }}>
                  Back to Order
                </button>
              </div>
            ) : clientSecret ? (
              <Elements stripe={stripePromise} options={{ clientSecret, appearance: STRIPE_APPEARANCE }}>
                <StripePaymentForm
                  cart={cart}
                  symbol={symbol}
                  onSuccess={() => setStep(CONFIRM_STEP)}
                  onBack={() => { setStep(needsMc ? 1 : 0); setClientSecret(null); setFetchError(null); }}
                />
              </Elements>
            ) : (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#6b6080", fontFamily: "'Cinzel',serif", fontSize: 11 }}>
                <div style={{ width: 20, height: 20, border: "2px solid rgba(123,94,167,.4)", borderTopColor: "#9b7fd4", borderRadius: "50%", animation: "co-spin .7s linear infinite", margin: "0 auto 12px" }} />
                Preparing secure payment...
              </div>
            )
          )}

          {step === CONFIRM_STEP && (
            <ConfirmStep cart={cart} symbol={symbol} onClose={onClose} />
          )}
        </div>
      </McFrame>
    </div>
  );
}